import React from 'react';
import { Link } from 'react-router-dom';

import {
  getLatestYoutubeVideos,
  type YoutubeVideo,
} from '../../../../services/api/youtubeVideos';

import {
  getCookieConsent,
  saveCookiePreferences,
} from '../../../../utils/cookieConsent';

import './VideoContentView.less';

type Props = {
  content?: any;
};

function getVideoIdFromUrl(url = '') {
  try {
    const parsed = new URL(url);
    return parsed.searchParams.get('v') || '';
  } catch {
    return '';
  }
}

function formatDate(date?: string | null) {
  if (!date) return '';

  if (date.includes('|')) {
    return date;
  }

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
}

export default function VideoContentView({ content }: Props) {
  const title = content?.title || '';
  const description = content?.description || '';
  const videoUrl = content?.video_link || '';
  const videoImage = content?.video_image || '';
  const videoId = getVideoIdFromUrl(videoUrl) || content?.id || '';
  const publishedDate = content?.effective || content?.created || '';

  const [relatedVideos, setRelatedVideos] = React.useState<YoutubeVideo[]>([]);
  const [canLoadYoutube, setCanLoadYoutube] = React.useState(false);

  React.useEffect(() => {
    const updateConsent = () => {
      const preferences = getCookieConsent();

      setCanLoadYoutube(!!preferences.externalMedia);
    };

    updateConsent();

    window.addEventListener('radiomec-cookie-updated', updateConsent);

    return () => {
      window.removeEventListener('radiomec-cookie-updated', updateConsent);
    };
  }, []);

  React.useEffect(() => {
    if (!videoId) return;

    let cancelled = false;

    async function loadRelatedVideos() {
      try {
        const data = await getLatestYoutubeVideos(0);

        if (cancelled) return;

        setRelatedVideos(
          data.filter((video) => video.id !== videoId).slice(0, 3),
        );
      } catch {
        if (!cancelled) {
          setRelatedVideos([]);
        }
      }
    }

    loadRelatedVideos();

    return () => {
      cancelled = true;
    };
  }, [videoId]);

  const handleAllowExternalMedia = () => {
    const currentConsent = getCookieConsent();

    saveCookiePreferences({
      ...currentConsent,
      externalMedia: true,
    });

    setCanLoadYoutube(true);
  };

  if (!videoId || !videoUrl) {
    return (
      <main className="video-content-view">
        <section className="video-content-view__state">
          <p>Vídeo não encontrado no site.</p>

          <Link to="/videocasts">Voltar para videocasts</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="video-content-view">
      <section
        className="video-content-view__hero"
        style={
          videoImage
            ? {
                backgroundImage: `linear-gradient(180deg, rgba(13, 24, 28, 0.76) 0%, rgba(13, 24, 28, 0.96) 68%, rgba(13, 24, 28, 1) 100%), url(${videoImage})`,
              }
            : undefined
        }
      >
        <div className="video-content-view__hero-inner">
          <span className="video-content-view__eyebrow">Videocast</span>

          <h1>{title}</h1>

          {publishedDate ? (
            <time dateTime={publishedDate}>{formatDate(publishedDate)}</time>
          ) : null}

          {description ? <p>{description}</p> : null}
        </div>
      </section>

      <section className="video-content-view__player-wrap">
        {canLoadYoutube ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}`}
            title={title}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <div className="video-content-view__blocked">
            <div className="video-content-view__blockedContent">
              <span className="video-content-view__blockedEyebrow">
                mídia externa
              </span>

              <h3>Conteúdo externo bloqueado</h3>

              <p>
                Este vídeo utiliza mídia incorporada do YouTube. Para assistir,
                permita o carregamento de mídia externa.
              </p>

              <button
                type="button"
                className="video-content-view__blockedButton"
                onClick={handleAllowExternalMedia}
              >
                Permitir mídia externa
              </button>
            </div>
          </div>
        )}
      </section>

      {relatedVideos.length > 0 ? (
        <section className="video-content-view__related-section">
          <div className="video-content-view__related-header">
            <span>Continue assistindo</span>
            <h2>Veja também</h2>
          </div>

          <div className="video-content-view__related-grid">
            {relatedVideos.map((video) => (
              <Link
                key={video.id}
                to={`/videocasts/${video.id}`}
                className="video-content-view__related-card"
              >
                <img src={video.thumbnail} alt="" loading="lazy" />

                <div>
                  {video.publishedAt ? (
                    <time>{formatDate(video.publishedAt)}</time>
                  ) : null}

                  <h3>{video.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
